// tslint:disable:max-line-length
import classNames from "classnames";
import React from "react";
import { Link } from "react-router";
import { isBrowser } from "../../utils/detection";

import "./Logo.scss";

interface Props {
  type: "accent" | "light" | "dark";

  /**
   * Width (and height) of the logo in pixels.
   */
  width?: number;

  /**
   * Additional classname to use as well as .logo.
   */
  className?: string;

  /**
   * A URL to use as a link. Uses React-Router's Link if relative.
   */
  link?: string;

  /**
   * If a link is provided, an onClick handler for the link element.
   */
  onClick?: () => void;

  /**
   * Additional styling for the container element.
   */
  style?: React.CSSProperties;
}

/**
 * TravelLocal's logo.
 * 
 * @status stable
 */
export class Logo extends React.Component<Props> {
  public static defaultProps = {
    type: "accent"
  };

  public render() {
    const { type, link, className, width, onClick, style } = this.props;

    // #1179 https://github.com/facebook/react/issues/7358 😿🔫
    const isIe = isBrowser() && (navigator.userAgent.indexOf("MSIE") !== -1 || navigator.appVersion.indexOf("Trident/") > -1);
    let logo: JSX.Element;
    if (isIe) {
      logo = (
        <div className={classNames("img-fallback", type)} />
      );
    }
    else {
      const diameter = width || 125;
      logo = (
        <svg
          className={type}
          viewBox={`0 0 ${diameter} ${diameter}`}
          preserveAspectRatio="xMinYMin"
        >
          <g fill="none" fillRule="evenodd" transform={`scale(${diameter / 125} ${diameter / 125})`}>
            <title>TravelLocal logo</title>
            <path
              className="logo-path-a"
              fill="#3DAE65"
              d="M104.103 71.325c-2.947 0-3.93 3.18-3.93 5.809 0 2.63.983 5.808 3.93 5.808 2.948 0 3.873-3.179 3.873-5.808 0-2.63-.867-5.809-3.873-5.809M39.198 48.184c-2.947 0-3.93 3.178-3.93 5.808s.983 5.809 3.93 5.809c2.948 0 3.873-3.18 3.873-5.809 0-2.63-.867-5.808-3.873-5.808M72.282 71.18c-2.658 0-4.017 2.486-4.017 5.954 0 3.468 1.359 5.953 4.017 5.953 2.659 0 4.017-2.485 4.017-5.953 0-3.468-1.358-5.953-4.017-5.953M74.545 52.865c.896-.058 1.156-.664 1.156-1.242 0-2.11-1.3-3.44-3.179-3.44-2.427 0-3.872 2.024-4.017 4.943l.202.202 5.838-.463z"
            />
            <path
              className="logo-path-b"
              fill="#3DAE65"
              d="M113.35 82.77l-4.392 1.79h-.867l-.289-.288v-1.3c-1.04.982-2.225 1.704-4.508 1.704-3.41 0-6.935-2.832-6.935-7.542 0-4.161 3.178-7.542 7.455-7.542 2.572 0 4.19 1.271 5 2.253h.144l1.676-1.849h.983l.289.29-.29 2.658v7.542c0 .752.492 1.127 1.157 1.127h.289l.289.29v.866zm-24.449 1.906c-3.757 0-7.167-2.774-7.167-7.542s3.786-7.542 7.745-7.542c1.705 0 3.208.375 4.335 1.01l.202.29.145 3.612-.232.232h-1.127l-.23-.232v-.52c0-1.676-1.243-2.803-3.035-2.803-2.89 0-3.988 2.86-3.988 5.693 0 2.832 1.647 5.26 4.566 5.26 1.85 0 3.265-.868 4.19-2.601l.29-.116.317.116.145.288c-.752 2.544-2.284 4.855-6.156 4.855zM80.616 60.9v-1.271l.23-.232h.348c.578 0 1.156-.578 1.156-1.156V44.427c0-.578-.578-1.156-1.156-1.156h-.29l-.288-.289v-.867l4.681-1.792h.867l.29.29-.29 2.658v14.97c0 .577.578 1.155 1.156 1.155h.347l.231.232v1.27l-.231.232h-6.82l-.231-.231zm-8.334 23.777c-4.276 0-7.831-3.265-7.831-7.542 0-4.277 3.555-7.542 7.831-7.542 4.277 0 7.832 3.265 7.832 7.542 0 4.277-3.555 7.542-7.832 7.542zm-8.952-35.51l-5.693 11.675-.318.29h-.549l-.346-.29-5.693-11.675c-.116-.289-.26-.578-.694-.578h-.722l-.231-.23v-1.273l.23-.23h6.676l.231.23v1.272l-.23.231h-.579c-.462 0-.664.232-.664.578 0 .174.058.405.173.636l3.034 6.56 3.15-6.56c.116-.231.174-.462.174-.636 0-.346-.231-.578-.665-.578h-.578l-.231-.23v-1.273l.231-.23h4.74l.23.23v1.272l-.23.231h-.723c-.434 0-.607.29-.723.578zm-.702 34.875l-.231.23H46.33l-.232-.23v-1.272l.232-.231h.924c.578 0 1.156-.578 1.156-1.156v-14.45c0-.577-.578-1.155-1.156-1.155h-.924l-.232-.231v-1.272l.232-.231h8.322l.231.231v1.272l-.23.231h-.983c-.578 0-1.156.578-1.156 1.156v14.45c0 .577.578 1.155 1.156 1.155h3.612c3.15 0 3.901-2.023 4.103-3.872l.232-.231h1.271l.174.23-.434 5.376zM43.186 61.419l-.289-.289v-1.3c-1.04.982-2.225 1.705-4.508 1.705-3.41 0-6.935-2.832-6.935-7.543 0-4.16 3.178-7.542 7.456-7.542 2.571 0 4.19 1.271 4.999 2.254h.144l1.676-1.85h.983l.29.29-.29 2.658v7.542c0 .752.491 1.127 1.156 1.127h.29l.288.29v.867l-4.393 1.791h-.867zM28.562 50.9c-1.271 0-2.225-.982-2.225-2.225-.925.578-1.792 2.196-1.792 4.22v5.345c0 .578.578 1.156 1.156 1.156h.347l.231.232v1.27l-.23.232h-6.821l-.231-.231v-1.271l.23-.232h.348c.578 0 1.156-.578 1.156-1.156v-7.282c0-.578-.578-1.156-1.156-1.156h-.29l-.288-.289v-.665l.23-.289 4.162-1.705h.867l.29.29-.29 2.138c.752-1.618 2.139-2.832 4.046-2.832 1.474 0 2.485.982 2.485 2.254 0 1.242-.925 2.196-2.225 2.196zm43.845-4.45c4.855 0 7.08 3.583 7.08 7.427l-.231.23-10.693.897c.318 2.283 2.168 3.814 4.798 3.814 2.427 0 4.219-1.011 5.461-2.89l.29-.115.317.116.144.288c-.953 3.353-3.612 5.318-7.05 5.318-4.509 0-7.63-3.324-7.63-7.658 0-4.19 3.381-7.427 7.514-7.427zm42.506 37.837l-.231-.231v-1.272l.231-.232h.347a1.16 1.16 0 0 0 1.157-1.157V67.572a1.16 1.16 0 0 0-1.157-1.157h-.289l-.29-.29v-.867l4.686-1.793h.868l.289.29-.29 2.66v14.98c0 .636.521 1.157 1.157 1.157a.449.449 0 0 0 .426-.306 62.399 62.399 0 0 0 3.18-19.232C125.277 28.55 97.988.56 63.526.008 36.545-.424 13.4 16.244 4.22 39.882a.752.752 0 0 0 .703 1.02h13.118l.231.23.434 5.376-.173.23H17.26l-.231-.23c-.202-1.85-.954-3.873-4.104-3.873h-.75V58.24c0 .636.52 1.156 1.155 1.156h.983l.23.232v1.27l-.23.232H5.99L5.76 60.9v-1.271l.23-.232h.926A1.16 1.16 0 0 0 8.07 58.24V42.635H6.27c-2.367 0-3.378 1.144-3.821 2.497A62.524 62.524 0 0 0 0 62.513 62.317 62.317 0 0 0 62.803 125c26.467-.126 49.043-16.703 58.033-40.03a.504.504 0 0 0-.47-.682h-5.453z"
            />
          </g>
        </svg>
      );
    }

    return (
      <div className={classNames("logo", className)} style={{ ...style, ...(width ? {width, height: width} : {}) }}>
        {!link && logo}
        {link && (
          link.startsWith("/") // https://github.com/ReactTraining/react-router/issues/1147
            ? <Link className="logo-link" to={link} onClick={onClick}>{logo}</Link>
            : <a className="logo-link" href={link} onClick={onClick}>{logo}</a>
        )}
      </div>
    );
  }
}
